<script setup lang="ts">
    import useIsMobile from '@/composables/useIsMobile';
import { useAuthStore } from '@/store/auth';
import { useNotificationStore } from '@/store/notification';
import type { InfiniteScrollCustomEvent, RefresherCustomEvent } from '@ionic/vue';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonImg,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSplitPane,
  IonToolbar,
  alertController,
  menuController,
} from '@ionic/vue';
import { Menu } from 'lucide-vue-next';
import { computed, defineProps, onMounted, useAttrs } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MobileMenu from '../ui/navigation/MobileMenu.vue';

    interface Props {
        title?: string;
        className?: string;
        ignoreHistory?: boolean;
        showBottomNav?: boolean;
        fullscreen?: boolean;
        hideNotification?: boolean;
        hideHeader?: boolean;
        hasRefresh?: boolean;
        withInfiniteScroll?: boolean;
    }

    const props = defineProps<Props>();
    const emit = defineEmits(['refresh', 'loadMore']);

    const router = useRouter();
    const route = useRoute();
    const authStore = useAuthStore();
    const notificationStore = useNotificationStore();
    const isMobile = useIsMobile();

    const showSidebar = computed(() => {
        // Follow mobile app pattern - auth state is managed at router level
        // No need to initialize auth here, just use the current state
        if (!authStore.isAuthenticated) return false;
        if (!route.name) return false;

        return !['verify-otp', 'password-setup'].includes(route.name.toString());
    });

    onMounted(() => {
        if (authStore.isAuthenticated) {
            notificationStore.fetchCounts();
        }
    });

    // Function to open the mobile menu (for small screens)
    const openMobileMenu = async () => {
        const menuId = isMobile.value ? 'mobile-side-panel' : 'desktop-menu';
        await menuController.open(menuId);
    };

    const navigateTo = (path: string) => {
        router.push(`/${path}`);
    };

    // Navigation functions
    const goToSearch = () => {
        router.push('/search');
    };

    const goToNotifications = () => {
        router.push('/notifications');
    };

    const handleLogout = async () => {
        const alert = await alertController.create({
            header: 'Logout',
            message: 'Are you sure you want to logout?',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Logout',
                    role: 'confirm',
                    handler: async () => {
                        try {
                            await authStore.logout();
                            router.push('/login');
                        } catch (error) {
                            console.error('Logout error:', error);
                        }
                    },
                },
            ],
        });

        await alert.present();
    };

    const handleRefresh = (event: RefresherCustomEvent) => {
        emit('refresh', event);
    };

    const ionInfinite = (event: InfiniteScrollCustomEvent) => {
        emit('loadMore');
        // Don't automatically complete the event - let the parent component decide
        // when to complete it based on if there's more data
        setTimeout(() => event.target.complete(), 1000);
    };
</script>

<template>
    <ion-page>
        <ion-header class="ion-no-border">
            <ion-toolbar
                v-if="!hideHeader"
                class="px-6"
            >
                <ion-img
                    src="/assets/motorcentral-logo.svg"
                    v-bind="useAttrs()"
                    class="h-5 w-33.75"
                />

                <!-- Menu button for small screens (sm and below) - on the right -->
                <ion-buttons
                    v-if="showSidebar && isMobile"
                    slot="end"
                >
                    <ion-button
                        fill="clear"
                        @click="openMobileMenu"
                    >
                        <Menu class="size-6 text-white" />
                    </ion-button>
                </ion-buttons>

                <!-- Notification and Logout buttons - hidden on small screens (sm and below) -->
                <ion-buttons
                    v-if="!isMobile"
                    slot="end"
                    class="flex items-center"
                >
                    <ion-button
                        fill="clear"
                        @click="router.push('/chat')"
                        class="!px-0"
                    >
                        <div class="flex h-11.5 w-11.5 items-center justify-center">
                            <ion-icon
                                slot="icon-only"
                                src="/assets/nav/ic-message.svg"
                                class="size-11.5"
                            />
                        </div>
                    </ion-button>
                    <ion-button
                        v-if="!hideNotification"
                        fill="clear"
                        @click="router.push('/notifications')"
                        class="!px-0 relative"
                    >
                        <div class="flex h-11.5 w-11.5 items-center justify-center">
                            <ion-icon
                                slot="icon-only"
                                src="/assets/nav/ic-notifications.svg"
                                class="size-6"
                            />
                        </div>
                        <span
                            v-if="notificationStore.totalUnread > 0"
                            class="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
                        >
                            {{ notificationStore.totalUnread > 99 ? '99+' : notificationStore.totalUnread }}
                        </span>
                    </ion-button>
                    <slot name="action-button" />
                </ion-buttons>
                <ion-buttons
                    v-if="!isMobile"
                    slot="end"
                    class="flex items-center"
                >
                    <ion-button
                        v-if="!hideNotification"
                        fill="clear"
                        @click="handleLogout()"
                        class="!px-0"
                    >
                        <div class="flex h-11.5 w-11.5 items-center justify-center">
                            <ion-icon
                                slot="icon-only"
                                src="/assets/nav/ic-logout.svg"
                                class="size-6"
                            />
                        </div>
                    </ion-button>
                    <slot name="action-button" />
                </ion-buttons>
            </ion-toolbar>

            <slot name="progress" />
        </ion-header>
        <ion-split-pane
            when="xs"
            content-id="main-content"
        >
            <!-- Mobile Menu for larger screens - rendered inside split pane as regular menu -->
            <mobile-menu
                v-if="showSidebar"
                class="hidden sm:block"
            />
            <ion-content
                id="main-content"
                :fullscreen="fullscreen"
                :class="`${className}`"
            >
                <ion-refresher
                    v-if="hasRefresh"
                    slot="fixed"
                    @ionRefresh="handleRefresh($event)"
                >
                    <ion-refresher-content></ion-refresher-content>
                </ion-refresher>
                <div class="ion-padding mt-[78px]">
                    <slot />
                </div>

                <ion-infinite-scroll
                    v-if="withInfiniteScroll"
                    @ionInfinite="ionInfinite"
                    threshold="0"
                >
                    <ion-infinite-scroll-content></ion-infinite-scroll-content>
                </ion-infinite-scroll>
            </ion-content>
        </ion-split-pane>

        <!-- Bottom Navigation -->
        <ion-footer v-if="$slots.footer">
            <ion-toolbar>
                <slot name="footer" />
            </ion-toolbar>
        </ion-footer>
    </ion-page>
</template>

<style scoped>
    ion-toolbar {
        --background: var(--ion-color-primary);
        --padding-top: 20px;
        --padding-bottom: 20px;
    }

    ion-toolbar.loan-application {
        --background: #ffffff;
        --padding-top: 20px;
        --padding-bottom: 20px;
    }

    ion-content {
        --background: #fafafa;
    }
</style>
